// src/components/core/migration/ImportDocumentModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState, useEffect, useCallback } from 'react';
import { ConfigProvider } from 'antd';
import { ImportOutlined } from '@ant-design/icons';
import { createRoot } from 'react-dom/client';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import appService from 'services/appService';
import authService from 'services/authService';
import axiosService from 'services/axiosService';
import marketplaceService from 'services/marketplaceService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import MigrationDocumentModalStep1 from './Step1';

// *******************************************************************************
// THEME WRAPPER COMPONENT
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;

  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const MigrationDocumentModal = ({ show, setShow, afterClose, onSuccess, collection = 'nfe' }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const storedModule = useZustand('module', 'config');

  // Credits state
  const [creditsBalance, setCreditsBalance] = useState(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);

  // Enrichment switches state (local, not saved)
  const [enrichCnpj, setEnrichCnpj] = useState(false);
  const [enrichCpf, setEnrichCpf] = useState(false);

  // Enrichment prices (fetched from API)
  const [enrichmentPrices, setEnrichmentPrices] = useState({
    recipientByCnpj: 0,
    recipientByCpf: 0,
  });

  // Get current module to find the correct migration token
  const currentModule = storedModule?.id || collection;

  // Get migration token settings
  const migrationToken = useMemo(() => {
    const migrationTokens = storedSubscription?.migrationTokens || [];
    return migrationTokens.find(t => t.collectionName === currentModule) || null;
  }, [storedSubscription?.migrationTokens, currentModule]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Initialize enrichment switches from migration token when modal opens
  useEffect(() => {
    if (show && migrationToken) {
      // Use collection to determine which field to read:
      // - 'nfe' (invoices): use enrichRecipientByCnpj/Cpf
      // - 'customers', 'suppliers': use enrichByCnpj/Cpf
      const isInvoicesContext = collection === 'nfe';

      const cnpjEnabled = isInvoicesContext
        ? (migrationToken.enrichRecipientByCnpj ?? false)
        : (migrationToken.enrichByCnpj ?? false);

      const cpfEnabled = isInvoicesContext
        ? (migrationToken.enrichRecipientByCpf ?? false)
        : (migrationToken.enrichByCpf ?? false);

      setEnrichCnpj(cnpjEnabled);
      setEnrichCpf(cpfEnabled);
    }
  }, [show, migrationToken, collection]);

  // Fetch credits balance when modal opens
  useEffect(() => {
    if (show && storedSubscription?._id) {
      const fetchCredits = async () => {
        setIsLoadingCredits(true);
        try {
          await marketplaceService.refreshCreditsBalance();
          const balance = storedSubscription?.jarvislyCredits?.balance || 0;
          setCreditsBalance(balance);
        } catch (error) {
          console.error('[ImportDocumentModal] Error fetching credits:', error);
          setCreditsBalance(0);
        } finally {
          setIsLoadingCredits(false);
        }
      };
      fetchCredits();
    }
  }, [show, storedSubscription?._id]);

  // Fetch enrichment prices from API
  useEffect(() => {
    if (!show) return;

    const fetchPrices = async () => {
      try {
        const [response] = await axiosService.get({
          url: API.MIGRATION.PRICES,
          returnAsObject: true,
        });

        if (response?.prices) {
          setEnrichmentPrices(response.prices);
        }
      } catch (error) {
        console.error('[ImportDocumentModal] Error fetching prices:', error);
      }
    };

    fetchPrices();
  }, [show]);

  // Update credits balance when subscription changes
  useEffect(() => {
    if (storedSubscription?.jarvislyCredits?.balance !== undefined) {
      setCreditsBalance(storedSubscription.jarvislyCredits.balance);
    }
  }, [storedSubscription?.jarvislyCredits?.balance]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, goFocus }) => {
    const legacyCode = form.getFieldValue('legacyCode');

    // Validate input
    if (!legacyCode) {
      // appService.message({ flag: 'w', text: translateX('import_order_code_required') });

      goFocus?.('legacyCode', true);
      return false;
    }

    // Show loading message
    const msg = await appService.message({
      flag: 'loading',
      text: translateX('importing_document'),
      delay: 0,
    });

    try {
      // Call the reimport API with enrichment options
      // Note: Manual migration NEVER overwrites existing documents
      // conflictStrategy is only used by automatic migration (MigratorTab)
      const response = await axiosService.post({
        url: API.MIGRATION.REIMPORT,
        data: {
          legacyCode: String(legacyCode),
          collection,
          enrichCnpj,
          enrichCpf,
          // conflictStrategy is intentionally NOT passed here
          // Manual migration always uses 'ignore' behavior (never overwrites)
        },
      });

      // Response is an array: [responseData, metadata]
      const [result] = response || [];

      // appService.messageDestroy();

      // Handle success scenarios
      if (result?.success) {
        // Check if document was overwritten (existed before and was updated)
        const wasOverwritten = result.wasOverwritten || false;

        msg.update({
          flag: 'success',
          text: wasOverwritten ? 'document_reimported' : 'document_imported',
        });
        // appService.notification({
        //   flag: 'success',
        //   title: translateX(wasOverwritten ? 'document_reimported' : 'document_imported'),
        //   description: translateX(
        //     wasOverwritten
        //       ? 'document_reimported_success_description'
        //       : 'document_imported_success_description',
        //     {
        //       documentNumber: <strong>{result.searchedOrderNumber || legacyCode}</strong>,
        //       company: <strong>{toCapitalCase(result.issuerName) || '-'}</strong>,
        //     }
        //   ),
        //   duration: 8,
        // });

        // Call success callback
        if (onSuccess) {
          await onSuccess(result);
        }

        // Show notification if credits were debited
        marketplaceService.notifyCreditsDebited({
          creditsDebited: result.creditsDebited,
          cpfCnpj: result.cpfCnpj || result.entityCpfCnpj || legacyCode,
          balanceAfter: result.balanceAfter,
        });

        // Refresh credits balance after import (enrichment may have deducted credits)
        await marketplaceService.refreshCreditsBalance();

        // Refresh auth to update companies list in case a new issuer was imported
        await authService.signInRefresh();

        return true;
      } else {
        msg.close();

        // Handle error codes from response (HTTP 200 but logical error)
        const errorCode = result?.errorCode;

        if (errorCode === 'UPDATE_NOT_ALLOWED') {
          appService.notification({
            flag: 'warning',
            title: translateX('document_imported_failure'),
            description: translateX('document_imported_failure_description', {
              documentNumber: <strong>{legacyCode}</strong>,
            }),
            duration: 10,
          });
        } else if (errorCode === 'INSUFFICIENT_CREDITS') {
          appService.notification({
            flag: 'warning',
            title: translateX('insufficient_credits'),
            description: translateX('insufficient_credits_for_enrichment'),
            duration: 8,
          });
        } else {
          appService.notification({
            flag: 'warning',
            title: translateX('document_not_found'),
            description: translateX('document_not_found_description', {
              documentNumber: <strong>{legacyCode}</strong>,
            }),
            duration: 8,
          });
        }

        goFocus?.('legacyCode', true);
        return false;
      }
    } catch (error) {
      msg.close();
      // appService.messageDestroy();

      const errorData = error?.response?.data?.data || error?.response?.data || {};
      const errorCode = errorData?.errorCode;

      if (errorCode === 'UPDATE_NOT_ALLOWED') {
        appService.notification({
          flag: 'warning',
          title: translateX('import_update_not_allowed_title'),
          description: translateX('import_update_not_allowed_description', {
            documentNumber: errorData.documentNumber || '-',
            serie: errorData.documentSerie || '-',
            status: errorData.existingStatusLabel || errorData.existingStatus || '-',
            recipient: errorData.recipientName || '-',
            issuer: errorData.issuerName || '-',
          }),
          duration: 10,
        });
      } else if (errorCode === 'DOCUMENT_NOT_FOUND') {
        appService.notification({
          flag: 'warning',
          title: translateX('import_order_not_found_title'),
          description: translateX('import_order_not_found_description', {
            number: legacyCode,
          }),
          duration: 8,
        });
      } else if (errorCode === 'INSUFFICIENT_CREDITS') {
        appService.notification({
          flag: 'warning',
          title: translateX('insufficient_credits'),
          description: translateX('insufficient_credits_for_enrichment'),
          duration: 8,
        });
      } else {
        const errorMessage = errorData?.message || error?.message;
        appService.notification({
          flag: 'error',
          title: translateX('import_order_error_title'),
          description: errorMessage || translateX('unknown_error'),
          duration: 8,
        });
      }

      goFocus?.('legacyCode', true);
      return false;
    }
  };

  const handleAfterClose = useCallback(() => {
    // Reset state when modal closes
    setEnrichCnpj(false);
    setEnrichCpf(false);
    setCreditsBalance(0);
    setIsLoadingCredits(true);
    afterClose?.();
  }, [afterClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Calculate required credits based on selected enrichments (1 credit = R$ 0.10)
  const cnpjCredits = Math.ceil((enrichmentPrices?.recipientByCnpj || 0) * 10);
  const cpfCredits = Math.ceil((enrichmentPrices?.recipientByCpf || 0) * 10);
  const requiredCredits = (enrichCnpj ? cnpjCredits : 0) + (enrichCpf ? cpfCredits : 0);
  const hasEnoughCredits = creditsBalance >= requiredCredits || requiredCredits === 0;

  // Disable confirm button if enrichment is selected but credits are insufficient
  const confirmDisabled = !hasEnoughCredits && (enrichCnpj || enrichCpf);

  const steps = useMemo(
    () => [
      MigrationDocumentModalStep1({
        enrichCnpj,
        setEnrichCnpj,
        enrichCpf,
        setEnrichCpf,
        creditsBalance,
        isLoadingCredits,
        enrichmentPrices,
      }),
    ],
    [enrichCnpj, enrichCpf, creditsBalance, isLoadingCredits, enrichmentPrices]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<ImportOutlined />}
      title="migrate_document"
      steps={steps}
      onFinish={handleFinish}
      afterClose={handleAfterClose}
      width={420}
      maskClosable={false}
      confirmDisabled={confirmDisabled}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION (creates modal in React root)
// *******************************************************************************
const showMigrationDocumentModal = ({ onSuccess, collection = 'nfe' } = {}) => {
  // Create container
  const container = document.createElement('div');
  container.id = 'import-order-modal-container';
  document.body.appendChild(container);

  // Create React root
  const root = createRoot(container);

  // Track modal open state
  let isModalOpen = true;

  // Render function to update modal state
  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <MigrationDocumentModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          onSuccess={onSuccess}
          collection={collection}
        />
      </ThemeWrapper>
    );
  };

  // Close handler: Sets show=false to trigger closing animation
  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  // AfterClose handler: Called after animation completes, then cleanup DOM
  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  // Initial render
  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default showMigrationDocumentModal;

export { MigrationDocumentModal };
